import React, { Suspense, lazy } from "react"
import { BrowserRouter as Router, Routes, Route, Outlet } from "react-router-dom"
import { AuthProvider } from "./contexts/AuthContext"
import ProtectedRoute from "./components/admin/ProtectedRoute"
import { Footer } from "./components/footer/Footer"
import { Header } from "./components/header/Header"
import { HotNews } from "./pages/common/HotNews"
import { home_folder, oldChurchSite } from "./assets/data/constants"
import { append } from "./components/i18n/i18n"
import { ThemeProvider } from "./contexts/ThemeContext"
import { storage } from "./utils/storage"

// Lazy helper for modules that use a named export instead of default.
const lazyNamed = (factory, name) => lazy(() => factory().then(m => ({ default: m[name] })))

// ── Admin section (default exports) — loaded only when an admin route opens ──
const AdminLogin    = lazy(() => import("./pages/admin/AdminLogin"))
const AdminRoles    = lazy(() => import("./pages/admin/AdminRoles"))
const AdminUsers    = lazy(() => import("./pages/admin/AdminUsers"))
const AdminPages    = lazy(() => import("./pages/admin/AdminPages"))
const AdminArchive  = lazy(() => import("./pages/admin/AdminArchive"))
const AdminVerses   = lazy(() => import("./pages/admin/AdminVerses"))
const AdminQuiz     = lazy(() => import("./pages/admin/AdminQuiz"))
const AdminSchedule = lazy(() => import("./pages/admin/AdminSchedule"))

// ── Public pages ─────────────────────────────────────────────────────────────
const Login      = lazyNamed(() => import("./pages/login/Login"), "Login")
const Account    = lazyNamed(() => import("./pages/account/Account"), "Account")
const WebArchive = lazyNamed(() => import("./pages/archive/WebArchive"), "WebArchive")
const Contact    = lazyNamed(() => import("./pages/common/Contact"), "Contact")
const Schedule   = lazyNamed(() => import("./pages/common/Schedule"), "Schedule")
const About      = lazyNamed(() => import("./pages/common/About"), "About")
const Help       = lazyNamed(() => import("./pages/common/Help"), "Help")
const NewsFeed   = lazyNamed(() => import("./pages/common/NewsFeed"), "NewsFeed")
const Resources  = lazyNamed(() => import("./pages/resources/Resources"), "Resources")
const Bible      = lazyNamed(() => import("./pages/bible/Bible"), "Bible")
const BibleQuiz  = lazyNamed(() => import("./pages/bible/BibleQuiz"), "BibleQuiz")
const YoutubeNews = lazy(() => import("./components/blog/YoutubeNews"))
const InstaFeed   = lazy(() => import("./components/blog/InstaFeed"))

// ── Resource articles — one data-driven page for all (see articlesContent.js) ──
const ArticlePage = lazyNamed(() => import("./pages/resources/ArticlePage"), "ArticlePage")

const PageFallback = () => (
  <div style={{ padding: '80px 20px', textAlign: 'center', color: '#9a7c63' }}>Завантаження…</div>
)

const PublicLayout = () => (
  <>
    <Header />
    <Suspense fallback={<PageFallback />}>
      <Outlet />
    </Suspense>
    <Footer />
  </>
)

const App = () => {
  const locale = storage.language.get()
  append(require(`./localization/${locale}.json`))

  return (
    <ThemeProvider>
    <AuthProvider>
      <Router basename={`${home_folder}`}>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Admin section — no Header/Footer */}
            <Route path='/admin/login' element={<AdminLogin />} />
            <Route path='/admin/roles' element={
              <ProtectedRoute permission='admin:roles'><AdminRoles /></ProtectedRoute>
            } />
            <Route path='/admin/users' element={
              <ProtectedRoute permission='admin:users'><AdminUsers /></ProtectedRoute>
            } />
            <Route path='/admin/pages' element={
              <ProtectedRoute permission='admin:pages'><AdminPages /></ProtectedRoute>
            } />
            <Route path='/admin/archive' element={
              <ProtectedRoute permission='admin:archive'><AdminArchive /></ProtectedRoute>
            } />
            <Route path='/admin/schedule' element={
              <ProtectedRoute permission='admin:schedule'><AdminSchedule /></ProtectedRoute>
            } />
            <Route path='/admin/verses' element={
              <ProtectedRoute permission='admin:pages'><AdminVerses /></ProtectedRoute>
            } />
            <Route path='/admin/quiz' element={
              <ProtectedRoute permission='admin:quiz'><AdminQuiz /></ProtectedRoute>
            } />
            <Route path='/admin' element={
              <ProtectedRoute><AdminPages /></ProtectedRoute>
            } />

            {/* Public site — wrapped with Header + Footer */}
            <Route element={<PublicLayout />}>
              <Route exact path='/' element={<HotNews />} />
              <Route path='/news/:id' element={<YoutubeNews />} />
              <Route exact path='/login' element={<Login />} />
              <Route exact path='/insta' element={<InstaFeed />} />
              <Route exact path='/account' element={<Account />} />
              <Route exact path='/contact' element={<Contact />} />
              <Route exact path='/about' element={<About />} />
              <Route exact path='/help' element={<Help />} />
              <Route exact path='/archive' element={<WebArchive />} />
              <Route exact path='/schedule' element={<Schedule />} />
              <Route exact path='/newsfeed' element={<NewsFeed />} />
              <Route exact path='/resources' element={<Resources />} />
              <Route exact path='/bible' element={<Bible />} />
              <Route exact path='/bible-quiz' element={<BibleQuiz />} />
              <Route exact path='/resources/pastoral-vocation' element={<ArticlePage slug='pastoral-vocation' />} />
              <Route exact path='/resources/the-gods-will' element={<ArticlePage slug='the-gods-will' />} />
              <Route exact path='/resources/nehemiah-formation' element={<ArticlePage slug='nehemiah-formation' />} />
              <Route exact path='/resources/4-resilience' element={<ArticlePage slug='4-resilience' />} />
              <Route exact path='/resources/5-whoweare' element={<ArticlePage slug='5-whoweare' />} />
              <Route exact path='/resources/6-priesthood' element={<ArticlePage slug='6-priesthood' />} />
              <Route exact path='/resources/7-faithful-message' element={<ArticlePage slug='7-faithful-message' />} />
              <Route exact path='/resources/8-i-have-a-dream' element={<ArticlePage slug='8-i-have-a-dream' />} />
              <Route exact path='/resources/9-path-milestones-king' element={<ArticlePage slug='9-path-milestones-king' />} />
              <Route exact path='/resources/10-enduring-life' element={<ArticlePage slug='10-enduring-life' />} />
              <Route exact path='/oldsite' element={() => {
                window.location.href = oldChurchSite
                return null
              }} />
            </Route>
          </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
    </ThemeProvider>
  )
}

export default App
